import type { TFunction } from "i18next";
import type { ProjectedFeature } from "@nexus/contracts";
import { featureDetails, featureDisplayState } from "@nexus/contracts";
import { RESULT_STATE_META, stateTone, type ResultTone } from "../constants/result-state";

// featureDisplayState / isFeatureVisible / visibleFeatures live in @nexus/contracts so the web
// UI, the client-facing exports, and the server-side PDF all share one definition and cannot
// drift (AD-1749). Re-exported here so existing preflight imports keep resolving from this lib.
export {
  featureDetails,
  featureDisplayState,
  isFeatureVisible,
  visibleFeatures,
} from "@nexus/contracts";

/** Semantic tone for a feature's DISPLAY state (see featureDisplayState). */
export function featureDisplayTone(feature: ProjectedFeature): ResultTone {
  return stateTone(featureDisplayState(feature));
}

/**
 * Stable DOM id for a criterion's breakdown row, so cross-views (heatmap cell) can scroll
 * to the exact row. Criterion keys can be free-text names, so non-id-safe chars are folded.
 */
export function criterionRowId(prefix: string, criterionKey: string): string {
  return `${prefix}-${criterionKey.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}

/** DS weights arrive as a 0–1 fraction (or already a percent); normalise to a percent string. */
export function weightPercent(weight: number, fractionDigits = 1): string {
  const pct = weight <= 1 ? weight * 100 : weight;
  return `${pct.toFixed(fractionDigits)}%`;
}

/** Short result label: the numeric fractional score for PARTIAL, the state label otherwise. */
export function featureResultLabel(
  feature: ProjectedFeature,
  t: TFunction,
): string {
  const state = featureDisplayState(feature);
  if (state === "PARTIAL" && feature.featureScore != null) {
    return feature.featureScore.toFixed(1);
  }
  const meta = RESULT_STATE_META[state];
  return meta.labelKey ? t(meta.labelKey) : t("preflight.dash");
}

/** Notes-column explanation: the state's canned note, else the DS sub-check details. */
export function featureNote(feature: ProjectedFeature, t: TFunction): string {
  const meta = RESULT_STATE_META[feature.state];
  if (meta.noteKey) return t(meta.noteKey);
  const details = featureDetails(feature).join(" · ");
  return details || t("preflight.dash");
}
